// BulkSmsModal.jsx
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import API from "../../services/base_url";
import { getAllUsers } from "../../hooks/admin_api";
import { useNotifications } from "../../contexts/NotificationContext";
import { toast } from "react-toastify";

const BulkSmsModal = ({ show, onHide, token, adminId, adminName }) => {
  const [message, setMessage] = useState("");
  const [recipients, setRecipients] = useState("all"); // 'all' or 'selected'
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const { triggerRefresh } = useNotifications();

  // Fetch users when modal opens
  useEffect(() => {
    if (show) {
      fetchUsers();
      // Reset states when modal opens
      setMessage("");
      setRecipients("all");
      setSelectedUsers([]);
      setResult(null);
      setError("");
    }
  }, [show]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setError("");

      console.log("Fetching users for SMS...");

      // Use your existing getAllUsers API
      const response = await getAllUsers(token, 1, 1000); // Adjust limit as needed

      console.log("Full API Response:", response);

      // Your getAllUsers returns res.data directly, which is { status: true, users: [...] }
      // So response is already { status: true, users: [...] }

      if (response?.status) {
        // The users array is directly in response.users (not response.data.users)
        const usersArray = response.users || [];

        console.log(`Found ${usersArray.length} total users in response`);

        // Filter users to only include those with phone numbers
        const usersWithPhones = usersArray.filter(
          (user) =>
            user &&
            user.phone &&
            typeof user.phone === "string" &&
            user.phone.trim() !== "" &&
            user.phone !== "null" &&
            user.phone !== null &&
            !user.phone.includes("example") // Optional: exclude test phones
        );

        console.log(
          `Filtered to ${usersWithPhones.length} users with phone numbers`
        );

        // Transform users to ensure they have id field (your API returns user_id)
        const transformedUsers = usersWithPhones.map((user) => ({
          ...user,
          id: user.user_id || user.id, // Use user_id as id for consistency
        }));

        console.log("Transformed users:", transformedUsers);

        setUsers(transformedUsers);

        if (transformedUsers.length === 0) {
          setError(
            "No users with valid phone numbers found. Please ensure users have phone numbers in their profiles."
          );
        }
      } else {
        const errorMsg = response?.error || "Failed to load users list";
        setError(errorMsg);
        console.error("API returned error:", response);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to load users list";
      setError(errorMessage);
      console.error("Error fetching users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      // Use the transformed id (user.user_id)
      setSelectedUsers(users.map((user) => user.id));
    }
  };

  // Update selected users when recipients changes to "all"
  useEffect(() => {
    if (recipients === "all" && users.length > 0) {
      setSelectedUsers(users.map((user) => user.id));
    } else if (recipients === "all") {
      setSelectedUsers([]);
    }
  }, [recipients, users]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      setError("Message is required");
      return;
    }

    if (recipients === "selected" && selectedUsers.length === 0) {
      setError("Please select at least one user");
      return;
    }

    if (message.length > 160) {
      setError("Message is too long (max 160 characters)");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const payload = {
        message: message,
        recipients: recipients,
        selectedUsers: recipients === "selected" ? selectedUsers : [],
      };

      console.log("Sending SMS with payload:", payload);

      const response = await API.post(
        "/welfare/welfare-system/send_bulk_sms",
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status) {
        setResult(response.data.results);
        setMessage("");

        // 🔴 Trigger refresh in Updates component
        triggerRefresh();

        if (recipients === "selected") {
          setSelectedUsers([]);
        }
        // You can add a success toast here if you want
        // Show success message
        toast.success(
          `SMS sent successfully! ${response.data.results.successful} delivered, ${response.data.results.failed} failed.`
        );
      } else {
        setError(response.data.message || "Failed to send SMS");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Network error. Please try again."
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const characterCount = message.length;
  const maxLength = 160;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="bi bi-chat-text me-2"></i>
          Send Bulk SMS
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Recipient Selection */}
          <div className="mb-4">
            <Form.Group>
              <Form.Label className="fw-bold">
                <i className="bi bi-people me-1"></i>
                Send to:
              </Form.Label>
              <Form.Select
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                className="mb-3"
              >
                <option value="all">
                  All Users with Phone Numbers ({users.length})
                </option>
                <option value="selected">Select Specific Users</option>
              </Form.Select>
            </Form.Group>

            {recipients === "selected" && (
              <div className="border rounded p-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="mb-0">
                      Select Users{" "}
                      <Badge bg="info" className="ms-2">
                        {selectedUsers.length} selected
                      </Badge>
                    </h6>
                    <small className="text-muted">
                      {users.length} users with phone numbers available
                    </small>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={users.length === 0}
                  >
                    {selectedUsers.length === users.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                </div>

                {loadingUsers ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading users...</span>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-3 text-muted">
                    <i className="bi bi-phone-slash fs-4 mb-2 d-block"></i>
                    No users with phone numbers found
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #dee2e6",
                      borderRadius: "4px",
                      padding: "10px",
                    }}
                  >
                    {users.map((user) => (
                      <Form.Check
                        key={user.id} // Use the transformed id
                        type="checkbox"
                        id={`user-${user.id}`}
                        label={
                          <div className="d-flex flex-column">
                            <strong>{user.full_name || user.email}</strong>
                            <small className="text-muted">
                              {user.phone} •{" "}
                              {user.department || "No department"}
                            </small>
                          </div>
                        }
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleUserSelect(user.id)}
                        className="mb-2"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Message Input */}
          <div className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold">
                <i className="bi bi-chat-left-text me-1"></i>
                Message:
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={maxLength}
                placeholder="Type your SMS message here... (Max 160 characters)"
                className="mb-2"
              />
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  SMS will be sent as plain text
                </small>
                <small
                  className={
                    characterCount > maxLength ? "text-danger" : "text-muted"
                  }
                >
                  {characterCount}/{maxLength}
                </small>
              </div>
            </Form.Group>
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Results Display */}
          {result && (
            <div className="alert alert-info mb-3">
              <h6 className="fw-bold">
                <i className="bi bi-check-circle me-2"></i>
                SMS Sent Successfully!
              </h6>
              <div className="row mt-2">
                <div className="col-4">
                  <div className="text-center">
                    <div className="fw-bold fs-5">
                      {result.total_recipients}
                    </div>
                    <small>Total</small>
                  </div>
                </div>
                {/* <div className="col-4">
                  <div className="text-center text-success">
                    <div className="fw-bold fs-5">{result.successful}</div>
                    <small>Successful</small>
                  </div>
                </div> */}
                {/* <div className="col-4">
                  <div className="text-center text-danger">
                    <div className="fw-bold fs-5">{result.failed}</div>
                    <small>Failed</small>
                  </div>
                </div> */}
              </div>

              {result.failed > 0 && (
                <div className="mt-3">
                  <small className="fw-bold">Failed Messages:</small>
                  <div
                    style={{
                      maxHeight: "100px",
                      overflowY: "auto",
                      fontSize: "12px",
                      backgroundColor: "#f8f9fa",
                      padding: "5px",
                      borderRadius: "4px",
                      marginTop: "5px",
                    }}
                  >
                    {result.details
                      ?.filter((detail) => detail.status === "failed")
                      .map((detail, index) => (
                        <div key={index} className="mb-1">
                          <span className="text-danger">{detail.name}</span>:{" "}
                          {detail.reason}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={
              loading ||
              !message.trim() ||
              (recipients === "selected" && selectedUsers.length === 0) ||
              (recipients === "all" && users.length === 0)
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Sending...
              </>
            ) : (
              <>
                <i className="bi bi-send me-2"></i>
                Send SMS{" "}
                {recipients === "all"
                  ? `to All (${users.length})`
                  : `to ${selectedUsers.length} Users`}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BulkSmsModal;
